import { useEffect, useState } from "react";
import { getPdaMenuList } from "src/apis/menuApi";
import { MenuDto } from "src/dtos/menu/menuDto";
import { useNavigate } from "react-router-dom";
import { CloseOutlined } from "@ant-design/icons";
import usePDAMenuStore from "src/store/pda/usePDAMenuStore";
import { logout } from "src/apis/authApi";
import { postProgramLog } from "src/apis/programLogApi";

    // 노트 아이콘 컴포넌트
    const NoteIcon = ({ size = 32, color = "#3b82f6" }: { size?: number; color?: string }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="3" width="16" height="18" rx="2" stroke={color} strokeWidth="2" fill="white"/>
            <line x1="7" y1="8" x2="17" y2="8" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="7" y1="12" x2="17" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="7" y1="16" x2="13" y2="16" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    );

    interface PDASidebarProps {
    isOpen: boolean;
    onClose: () => void;
    onMenuClick?: (menu: MenuDto) => void;
    }

    /**
     * PDA 사이드바 컴포넌트
     * 메뉴 목록을 일렬로 표시
     */
    const PDASidebar = ({ isOpen, onClose, onMenuClick }: PDASidebarProps) => {
    const [menuList, setMenuList] = useState<MenuDto[]>([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isOpen) {
        fetchMenus();
        }
    }, [isOpen]);

    const fetchMenus = async () => {
        try {
        setLoading(true);
        const response = await getPdaMenuList();
        const flatMenus = response?.data?.data || [];
        
        // useYn이 'Y'인 메뉴만 필터링하고 menuSeq 기준으로 정렬
        const activeMenus = flatMenus
            .filter(menu => menu.useYn === 'Y')
            .sort((a, b) => a.menuSeq - b.menuSeq);
        
        setMenuList(activeMenus);
        } catch (error) {
        console.error("메뉴 조회 실패:", error);
        setMenuList([]);
        } finally {
        setLoading(false);
        }
    };

    const { setSelectedMenu } = usePDAMenuStore();

    const handleMenuClick = (menu: MenuDto) => {
        // 전역 상태에 메뉴 선택 저장
        setSelectedMenu(menu);

        // PDA 메뉴 진입 로그 (fire-and-forget)
        if (menu.menuCd) {
            void postProgramLog({
                programId: menu.menuCd,
                actionType: "OPEN",
                deviceType: "PDA",
            });
        }
        
        if (onMenuClick) {
        onMenuClick(menu);
        }
        onClose();
    };

    const clearTokensAndNavigate = () => {
        localStorage.removeItem("access-token");
        localStorage.removeItem("refresh-token");
        localStorage.removeItem("access-exp");
        localStorage.removeItem("refresh-exp");
        navigate("/signin");
    };

    const handleLogout = async () => {
        try {
            await logout();
        } catch (error) {
            console.warn("로그아웃 API 호출 실패:", error);
        } finally {
            clearTokensAndNavigate();
        }
    };

    if (!isOpen) return null;

    return (
        <>
        {/* 오버레이 */}
        <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
        />
        
        {/* 사이드바 */}
        <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-xl z-50 flex flex-col">
            {/* 헤더 */}
            <div className="bg-blue-dark text-white px-4 py-3 flex items-center justify-between">
            <h2 className="text-lg font-medium">메뉴</h2>
            <button
                onClick={onClose}
                className="p-1 hover:bg-blue-dark rounded transition-colors"
            >
                <CloseOutlined className="text-xl" />
            </button>
            </div>

            {/* 메뉴 목록 */}
            <div className="flex-1 overflow-y-auto">
            {loading ? (
                <div className="flex items-center justify-center py-8">
                <p className="text-gray-500">로딩 중...</p>
                </div>
            ) : menuList.length > 0 ? (
                <div className="py-2">
                {menuList.map((menu) => (
                    <button
                    key={menu.menuCd}
                    onClick={() => handleMenuClick(menu)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors border-b border-gray-200 flex items-center justify-between"
                    >
                    <div className="flex items-center gap-3">
                        {menu.menuCd && ['$$$', 'MST', 'ACC', 'TQC', 'HRM', 'SAL', 'SYS', 'EQM', 'PRD', 'WMS', 'PUR'].includes(menu.menuCd) ? (
                        <img
                            src={`${process.env.PUBLIC_URL}/MESICON/${menu.menuCd}.png`}
                            alt={menu.menuCd.toLowerCase()}
                            className="w-8 h-8 object-contain"
                        />
                        ) : (
                        <NoteIcon size={32} color="#3b82f6" />
                        )}
                        <div className="flex flex-col">
                        <span className="text-sm font-medium text-gray-900">{menu.menuNm}</span>
                        {menu.menuCd && (
                            <span className="text-xs text-gray-500">{menu.menuCd}</span>
                        )}
                        </div>
                    </div>
                    <span className="text-gray-400">›</span>
                    </button>
                ))}
                </div>
            ) : (
                <div className="flex items-center justify-center py-8">
                <p className="text-gray-500">메뉴가 없습니다.</p>
                </div>
            )}
            </div>

            {/* 푸터 */}
            <div className="border-t border-gray-200 p-4">
            <button
                onClick={handleLogout}
                className="w-full bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded transition-colors"
            >
                로그아웃
            </button>
            </div>
        </div>
        </>
    );
    };

    export default PDASidebar;

